/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import Game from '../../Game/Game';
import BetManager from '../../Playbar/BetManager';
import Communicator from '../../Server/Communicator';
import StateDependencyComponent from '../../StateManager/StateDependencyComponent';
import StateEvent from '../../StateManager/StateEvent';
import { StateID } from '../../StateManager/StateID';
import StateReference from '../../StateManager/StateReference';
import ClassUtil from '../ClassUtil';
import InjectableComponent from './ComponentReference/InjectableComponent';
import ReferencedComponent from './ComponentReference/ReferencedComponent';
import { ReferenceGroup } from './ComponentReference/ReferenceGroup';
import { ContextKey } from './Configurator/ContextKey';
import ObservableStringComponent from './Observable/ObservableStringComponent';
import IPopupManager from './Popup/IPopupManager';
import PopupManager from './Popup/PopupManager';
import { PrizeForce } from './PrizeForce';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

@cc._decorator.ccclass
export default class DemoManager extends StateDependencyComponent
{
    private _betManagerRef: InjectableComponent<BetManager> = new InjectableComponent();
    private _communicatorRef: InjectableComponent<Communicator> = new InjectableComponent();
    private _gameRef: InjectableComponent<Game> = new InjectableComponent<Game>();
    private _popupManagerRef: InjectableComponent<IPopupManager> = new InjectableComponent();
    private _prizeForcesRef: InjectableComponent<ReferencedComponent>
        = new InjectableComponent(ReferenceGroup.MainGame, "Demo.PrizeForces");

    @property({ type: cc.Node, visible: true })
    private _landscapeDemoScreen: cc.Node = null;

    @property({ type: cc.Node, visible: true })
    private _portraitDemoScreen: cc.Node = null;

    @property({ type: [cc.Layout], visible: true})
    private _prizeForceLayouts: cc.Layout[] = [];

    @property({ type: cc.Prefab, visible: true })
    private _prizeForceButton: cc.Prefab = null;

    @property({ type: ObservableStringComponent, visible: true })
    private _explicitPrizeIndex: ObservableStringComponent = null;

    @property({ type: ObservableStringComponent, visible: true })
    private _explicitBranchIndex: ObservableStringComponent = null;

    @property({ type: ObservableStringComponent, visible: true })
    private _explicitStopIndex: ObservableStringComponent = null;

    @property({ type: ObservableStringComponent, visible: true })
    private _explicitBonusList: ObservableStringComponent = null;

    @property({ type: InjectableComponent, visible: true })
    private _landscapePopupRef: InjectableComponent<PopupManager>
        = new InjectableComponent(ReferenceGroup.MainGame, "PopupManager.Landscape");

    @property({ type: InjectableComponent, visible: true })
    private _portraitPopupRef: InjectableComponent<PopupManager>
        = new InjectableComponent(ReferenceGroup.MainGame, "PopupManager.Portrait");

    private _initState: StateReference = new StateReference(StateID.CheckRecall);

    private _prizeForces: PrizeForce[] = [];

    private _shouldDemoSpin: boolean = false;
    public get ShouldDemoSpin(): boolean { return this._shouldDemoSpin; }

    private _currentPrizeForce: PrizeForce = null;

    /** @hidden */
    protected setContexts(): void
    {
        super.setContexts();
        this._betManagerRef.SetContext(BetManager);
        this._communicatorRef.SetContext(Communicator);
        this._gameRef.SetContext(Game);
        this._popupManagerRef.SetContext(ContextKey.POPUP_MANAGER);
        this._prizeForcesRef.SetContext(ReferencedComponent);
        this._landscapePopupRef.SetContext(PopupManager);
        this._portraitPopupRef.SetContext(PopupManager);
    }

    /** @hidden */
    protected start(): void
    {
        super.start(this._initState);
    }

    protected onStateChangeHandler = (event: StateEvent) =>
    {
        if (event.State === this._initState.State)
        {
            this.updatePrizeForces();
            this._landscapePopupRef.WithValue(popupManager => popupManager.AddPage(this._landscapeDemoScreen));
            this._portraitPopupRef.WithValue(popupManager => popupManager.AddPage(this._portraitDemoScreen));
        }
    }

    public RequestDemoSpin(onResult: (result: string) => void): void
    {
        this._shouldDemoSpin = false;
        this._communicatorRef.Value.ForcePlayResult(
            this._currentPrizeForce.PrizeIndex,
            this._currentPrizeForce.BranchIndex,
            this._currentPrizeForce.StopDataIndex,
            this._currentPrizeForce.BonusScriptList,
            onResult);
    }

    public SetupDemoSpin(prizeForceId: string): void
    {
        this._shouldDemoSpin = true;
        this._currentPrizeForce = this._prizeForces[prizeForceId];
        this._betManagerRef.WithValue(mgr => mgr.SetMaxBet());
        this._popupManagerRef.WithValue(mgr => mgr.Disable());
        this._gameRef.WithValue(game => game.OnSpinButtonClicked(null));
    }

    public BonusSpin(): void
    {
        this._shouldDemoSpin = true;
        this._gameRef.Value.OnSpinButtonClicked(null);
    }

    public ToggleStatsDisplay(): void
    {
        cc.director.setDisplayStats(!cc.director.isDisplayStats());
    }

    private updatePrizeForces(): void
    {
        this._prizeForces = this._prizeForcesRef.Value.getComponentsInChildren(PrizeForce);

        for (let prizeForceLayout of this._prizeForceLayouts)
        {
            let currentLayout: cc.Layout = null;
            let lastParent: cc.Node = null;

            let count: number = 0;
            for (let prizeForceId in this._prizeForces)
            {
                let prizeForce: PrizeForce = this._prizeForces[prizeForceId];

                if (lastParent !== prizeForce.node.parent)
                {
                    lastParent = prizeForce.node.parent;

                    if (currentLayout) { currentLayout.updateLayout(); }
                    currentLayout = new cc.Node().addComponent(cc.Layout);

                    currentLayout.resizeMode = cc.Layout.ResizeMode.CONTAINER;
                    currentLayout.type = cc.Layout.Type.GRID;
                    currentLayout.startAxis = cc.Layout.AxisDirection.HORIZONTAL;
                    currentLayout.node.width = prizeForceLayout.node.width * .95;
                    currentLayout.spacingX = prizeForceLayout.spacingX;
                    currentLayout.spacingY = prizeForceLayout.spacingY;

                    prizeForceLayout.node.addChild(currentLayout.node);
                }

                const button: cc.Button = cc.instantiate(this._prizeForceButton).getComponent(cc.Button);
                button.node.color = prizeForce.node.parent.color;

                const label: cc.Label = button.getComponentInChildren(cc.Label);
                if (label)
                {
                    label.string = prizeForce.Name;
                }

                this.AddComponentEventHandler(
                    ClassUtil.getName(DemoManager),
                    `prize-force-${count++}`,
                    () => this.SetupDemoSpin(prizeForceId),
                    button.clickEvents
                );

                currentLayout.node.addChild(button.node);
            }

            if (currentLayout) { currentLayout.updateLayout(); }
            prizeForceLayout.updateLayout();
        }
    }

    private setupExplicitPrize(): void
    {
        this._shouldDemoSpin = true;
        this._currentPrizeForce = new PrizeForce();
        this._currentPrizeForce.PrizeIndex = parseInt(this._explicitPrizeIndex.Value, 10);
        this._currentPrizeForce.BranchIndex = parseInt(this._explicitBranchIndex.Value, 10);
        this._currentPrizeForce.StopDataIndex = parseInt(this._explicitStopIndex.Value, 10);
        this._currentPrizeForce.BonusScriptList
            = this._explicitBonusList.Value.split(",").map(x => x === '' ? -1 : parseInt(x, 10));
        this._popupManagerRef.Value.Disable();
        this._gameRef.Value.OnSpinButtonClicked(null);
    }
}
